import bbox from "@turf/bbox";
import { feature } from "@turf/helpers";
import type { Feature, Polygon } from "geojson";

import type { Project } from "../../types/project";

/** [west, south, east, north] in degrees. */
export type Bbox = [number, number, number, number];

/**
 * A project's extent: the server-side `bbox` when the list payload carries
 * it, else computed from the boundary. Never `utils/boundaryUtils`'s
 * bboxFromBoundary, which reads the first ring only and is wrong for the
 * MultiPolygon boundaries that expanded projects have.
 */
export function projectBbox(project: Project): Bbox | null {
  const b = project.bbox;
  if (b && b.length === 4 && b.every((v) => Number.isFinite(v))) {
    return [b[0], b[1], b[2], b[3]];
  }
  if (!project.boundary) return null;
  try {
    const [w, s, e, n] = bbox(project.boundary as unknown as Polygon);
    return [w, s, e, n].every((v) => Number.isFinite(v)) ? [w, s, e, n] : null;
  } catch {
    return null;
  }
}

export function unionBbox(boxes: Array<Bbox | null>): Bbox | null {
  let out: Bbox | null = null;
  for (const b of boxes) {
    if (!b) continue;
    out = out
      ? [Math.min(out[0], b[0]), Math.min(out[1], b[1]), Math.max(out[2], b[2]), Math.max(out[3], b[3])]
      : [b[0], b[1], b[2], b[3]];
  }
  return out;
}

export function bboxOfProjects(projects: Project[]): Bbox | null {
  return unionBbox(projects.map(projectBbox));
}

export function bboxPolygon(b: Bbox): Feature<Polygon> {
  const [w, s, e, n] = b;
  return feature({
    type: "Polygon",
    coordinates: [[[w, s], [e, s], [e, n], [w, n], [w, s]]],
  });
}

/** True when the two extents overlap (touching counts). */
export function bboxIntersects(a: Bbox, b: Bbox): boolean {
  return a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];
}
